import React, { useState } from 'react';
import { useReminders } from '../hooks/useReminders';
import { useProjects } from '../hooks/useProjects';
import { useCompanies } from '../hooks/useCompanies';
import { Reminder } from '../types';
import { Button } from '../components/ui/Button';
import { Input, Textarea } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { TrashIcon } from '../components/icons/TrashIcon';
import { CheckIcon } from '../components/icons/CheckIcon';

export const LembretesPage: React.FC = () => {
    const { reminders, saveReminder, deleteReminder, toggleCompleted, loading } = useReminders();
    const { projects } = useProjects();
    const { companies } = useCompanies();
    const [isAdding, setIsAdding] = useState(false);
    const [formData, setFormData] = useState<Partial<Reminder>>({});

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await saveReminder(formData as Reminder);
            setIsAdding(false);
            setFormData({});
        } catch (error) {
            console.error('Failed to save reminder', error);
            alert('Erro ao salvar lembrete');
        }
    };

const safeGetTime = (dateStr: string | undefined | null) => {
        if (!dateStr) return 0;
        const slice = dateStr.slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(slice)) return 0;
        const d = new Date(slice + 'T00:00:00');
        if (isNaN(d.getTime())) return 0;
        return d.getTime();
    };

    const activeReminders = reminders.filter(r => !r.isCompleted).sort((a, b) => safeGetTime(a.dueDate) - safeGetTime(b.dueDate));
    const completedReminders = reminders.filter(r => r.isCompleted).sort((a, b) => safeGetTime(b.dueDate) - safeGetTime(a.dueDate));

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Anotações e Lembretes</h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                        Gerencie anotações e lembretes para entrar em contato com as obras.
                    </p>
                </div>
                <Button onClick={() => setIsAdding(!isAdding)} variant="primary">
                    {isAdding ? 'Cancelar' : 'Novo Lembrete'}
                </Button>
            </div>

            {isAdding && (
                <div className="bg-white dark:bg-dark-secondary p-6 rounded-xl border border-gray-200 dark:border-dark-border mb-8">
                    <h2 className="text-lg font-bold mb-4">Adicionar Lembrete</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                                label="Título"
                                required
                                value={formData.title || ''}
                                onChange={e => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Garantir extensão do aluguel"
                            />
                            <Input
                                label="Data Limite"
                                type="date"
                                required
                                value={formData.dueDate || ''}
                                onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
                            />
                        </div>
                        <Select
                            label="Vincular a uma Obra (Opcional)"
                            value={formData.projectId || ''}
                            onChange={e => setFormData({ ...formData, projectId: e.target.value })}
                            options={projects.map(p => {
                                const company = companies.find(c => c.id === p.companyId);
                                const city = company?.address?.city;
                                return { 
                                    value: p.id, 
                                    label: city ? `${p.name} - ${city}` : p.name 
                                };
                            })}
                            placeholderOption="Selecione uma obra"
                        />
                        <Textarea
                            label="Descrição / Detalhes"
                            value={formData.description || ''}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Anotações adicionais, contatos, etc."
                            rows={3}
                        />
                        <div className="flex justify-end pt-2">
                            <Button type="submit" variant="primary">Salvar Lembrete</Button>
                        </div>
                    </form>
                </div>
            )}

            <div className="mb-8">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-dark-border pb-2">Pendentes</h2>
                {activeReminders.length === 0 ? (
                    <p className="text-gray-500 italic">Nenhum lembrete pendente.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {activeReminders.map(reminder => (
                            <ReminderCard 
                                key={reminder.id} 
                                reminder={reminder} 
                                toggleCompleted={toggleCompleted} 
                                deleteReminder={deleteReminder}
                                projects={projects}
                                companies={companies}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-dark-border pb-2">Concluídos</h2>
                {completedReminders.length === 0 ? (
                    <p className="text-gray-500 italic">Nenhum lembrete concluído.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-75">
                        {completedReminders.map(reminder => (
                            <ReminderCard 
                                key={reminder.id} 
                                reminder={reminder} 
                                toggleCompleted={toggleCompleted} 
                                deleteReminder={deleteReminder}
                                projects={projects}
                                companies={companies}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const ReminderCard: React.FC<{ reminder: Reminder, toggleCompleted: any, deleteReminder: any, projects: any[], companies: any[] }> = ({ reminder, toggleCompleted, deleteReminder, projects, companies }) => {
    const dueDateStr = reminder.dueDate && typeof reminder.dueDate === 'string' ? reminder.dueDate.slice(0, 10) : '';
    const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(dueDateStr);
    
    let isOverdue = false;
    let isDueToday = false;
    let formattedDate = '';

    if (isValidDate) {
        const dueDateObj = new Date(dueDateStr + 'T00:00:00');
        const todayStarts = new Date(new Date().setHours(0,0,0,0));
        isOverdue = !reminder.isCompleted && dueDateObj < todayStarts;
        isDueToday = !reminder.isCompleted && dueDateStr === new Date().toISOString().slice(0, 10);
        try {
            formattedDate = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(dueDateStr + 'T12:00:00'));
        } catch (e) {
            formattedDate = dueDateStr;
        }
    }

    const project = projects.find(p => p.id === reminder.projectId);
    const projectName = project?.name;
    const projectCity = companies.find(c => c.id === project?.companyId)?.address?.city;

    return (
        <div className={`p-4 rounded-xl border relative shadow-sm transition-all
            ${reminder.isCompleted ? 'bg-gray-50 border-gray-200 dark:bg-gray-800/20 dark:border-dark-border' : 
              isOverdue ? 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800' : 
              isDueToday ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800' : 
              'bg-white border-gray-200 dark:bg-dark-surface dark:border-dark-border'}`}>
            
            <div className="flex justify-between items-start mb-2">
                <h3 className={`font-semibold ${reminder.isCompleted ? 'line-through text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
                    {reminder.title}
                </h3>
                <div className="flex items-center space-x-1">
                    <button onClick={() => toggleCompleted(reminder.id, !reminder.isCompleted)} 
                        className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 ${reminder.isCompleted ? 'text-green-500' : 'text-gray-400'}`}
                        title={reminder.isCompleted ? 'Desmarcar' : 'Marcar como concluído'}>
                        <CheckIcon />
                    </button>
                    <button onClick={() => { if(window.confirm('Excluir este lembrete?')) deleteReminder(reminder.id); }} 
                        className="p-1.5 rounded-md hover:bg-red-50 text-red-400 hover:text-red-600">
                        <TrashIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>
            
            <div className={`text-xs font-semibold mb-2 inline-flex items-center px-2 py-1 rounded-full
                ${reminder.isCompleted ? 'bg-gray-100 text-gray-600' : 
                  isOverdue ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' : 
                  isDueToday ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400' : 
                  'bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300'}`}>
                {reminder.isCompleted ? 'Concluído' :
                 isOverdue ? 'Atrasado' : 
                 isDueToday ? 'Vence Hoje' : 
                 formattedDate}
            </div>

            {projectName && (
                <div className="text-sm font-medium text-brand-blue dark:text-brand-lime flex items-center gap-1 my-1">
                    <span className="opacity-70 text-xs">Obra:</span> {projectName} {projectCity ? `- ${projectCity}` : ''}
                </div>
            )}
            
            {reminder.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 whitespace-pre-wrap">
                    {reminder.description}
                </p>
            )}
        </div>
    );
};
